import { useEffect, useMemo, useState } from "react";
import {FourPointStar, CrookedStar, ConsultationImage, ScholarshipImage, IeltsImage, ExclusiveGroupImage, TestimoniImage} from "../../../assets";
import BlogCard from "../BlogCard";

const fallbackImages = [
  ConsultationImage,
  ScholarshipImage,
  IeltsImage,
  ExclusiveGroupImage,
  TestimoniImage,
];

const badgeClasses = ["bg-[#e93c24]", "bg-[#1db27b]", "bg-[#f08f17]", "bg-[#2d5de7]", "bg-[#8a3ef0]", "bg-[#df2b78]"];

const POSTS_PER_PAGE = 9;
const TABLET_POSTS_PER_PAGE = 8;
const PAGE_CACHE_LIMIT = 5;
const PAGE_CACHE_STORAGE_KEY = "blog_posts_page_cache_v1";
const CATEGORY_CACHE_STORAGE_KEY = "blog_categories_cache_v1";
const SEARCH_DEBOUNCE_MS = 300;

const stripHtml = (value = "") =>
  String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&#8217;/g, "'")
    .replace(/&#8220;/g, '"')
    .replace(/&#8221;/g, '"')
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();

const formatDate = (value) => {
  if (!value) return "";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(parsed);
};

const estimateReadTimeFromContent = (content = "") => {
  const wordCount = stripHtml(content)
    .split(/\s+/)
    .filter(Boolean).length;

  return `${Math.max(1, Math.ceil(wordCount / 200))} menit`;
};

const normalizeReadTimeLabel = (value = "") =>
  String(value)
    .trim()
    .replace(/\bmenit\b/i, "min")
    .replace(/\bminutes?\b/i, "min");

const getWordPressReadTime = (post) => {
  const yoastReadTime = stripHtml(post?.yoast_head_json?.twitter_misc?.["Estimasi waktu membaca"] || "");

  if (yoastReadTime) {
    return normalizeReadTimeLabel(yoastReadTime);
  }

  return normalizeReadTimeLabel(estimateReadTimeFromContent(post?.content?.rendered || ""));
};

const normalizeWordPressBaseUrl = (value = "") => {
  const trimmed = value.trim().replace(/\/$/, "");

  if (!trimmed) {
    return "";
  }

  if (trimmed.includes("/wp-json")) {
    return trimmed;
  }

  return `${trimmed}/wp-json/wp/v2`;
};

const readJsonCache = (storageKey, fallbackValue) => {
  if (typeof window === "undefined") {
    return fallbackValue;
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);

    if (!rawValue) {
      return fallbackValue;
    }

    return JSON.parse(rawValue);
  } catch {
    return fallbackValue;
  }
};

const writeJsonCache = (storageKey, value) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // Ignore storage quota or private mode failures.
  }
};

const normalizeSearchTerm = (value = "") => String(value).trim().toLowerCase().replace(/\s+/g, " ");

const getResponsivePostsPerPage = (width) => (width >= 768 && width < 1024 ? TABLET_POSTS_PER_PAGE : POSTS_PER_PAGE);

const getPageCacheKey = (categoryId, searchTerm, page, postsPerPage) =>
  `${categoryId || "all"}:${normalizeSearchTerm(searchTerm) || "all"}:${page}:${postsPerPage}`;

const readPageCacheState = () => readJsonCache(PAGE_CACHE_STORAGE_KEY, { entries: [] });

const readPageCacheEntry = (categoryId, searchTerm, page, postsPerPage) => {
  const state = readPageCacheState();
  const cacheKey = getPageCacheKey(categoryId, searchTerm, page, postsPerPage);
  const cacheIndex = state.entries.findIndex((entry) => entry.key === cacheKey);

  if (cacheIndex === -1) {
    return null;
  }

  const entry = state.entries[cacheIndex];
  const nextEntries = [...state.entries.slice(0, cacheIndex), ...state.entries.slice(cacheIndex + 1), { ...entry, touchedAt: Date.now() }];

  writeJsonCache(PAGE_CACHE_STORAGE_KEY, { entries: nextEntries });

  return entry;
};

const writePageCacheEntry = (entry) => {
  const state = readPageCacheState();
  const nextEntries = state.entries.filter((existingEntry) => existingEntry.key !== entry.key);

  nextEntries.push(entry);

  while (nextEntries.length > PAGE_CACHE_LIMIT) {
    nextEntries.shift();
  }

  writeJsonCache(PAGE_CACHE_STORAGE_KEY, { entries: nextEntries });
};

const mapWordPressPost = (post, index) => {
  const featuredImage = post?._embedded?.["wp:featuredmedia"]?.[0]?.source_url;
  const categoryName = post?._embedded?.["wp:term"]?.[0]?.[0]?.name || "Blog";
  const excerpt = stripHtml(post?.excerpt?.rendered || "");

  return {
    image: featuredImage || fallbackImages[index % fallbackImages.length],
    badge: categoryName.toUpperCase(),
    badgeClass: badgeClasses[index % badgeClasses.length],
    readTime: getWordPressReadTime(post),
    title: stripHtml(post?.title?.rendered || "Untitled"),
    excerpt: excerpt || "Artikel ini belum memiliki ringkasan.",
    date: formatDate(post?.date),
    href: post?.link || "/blog",
    category: categoryName,
  };
};

const isPublishedPost = (post) => post?.status === "publish";

const fetchWordPressCategories = async (wordpressBaseUrl, signal) => {
  const queryParams = new URLSearchParams({
    hide_empty: "1",
    per_page: "100",
    orderby: "count",
    order: "desc",
  });

  const response = await fetch(`${wordpressBaseUrl}/categories?${queryParams.toString()}`, {
    signal,
  });

  if (!response.ok) {
    throw new Error(`WordPress categories request failed: ${response.status}`);
  }

  const data = await response.json();

  return Array.isArray(data)
    ? data
        .filter((category) => Number(category?.count || 0) > 0)
        .map((category) => ({
          id: category.id,
          label: stripHtml(category.name || ""),
        }))
    : [];
};

const fetchWordPressPostsPage = async (wordpressBaseUrl, page, categoryId, searchTerm, postsPerPage, signal) => {
  const queryParams = new URLSearchParams({
    _embed: "1",
    per_page: String(postsPerPage),
    page: String(page),
    orderby: "date",
    order: "desc",
  });

  if (categoryId) {
    queryParams.set("categories", String(categoryId));
  }

  if (searchTerm) {
    queryParams.set("search", searchTerm);
  }

  const response = await fetch(
    `${wordpressBaseUrl}/posts?${queryParams.toString()}`,
    {
      signal,
    }
  );

  if (!response.ok) {
    throw new Error(`WordPress request failed: ${response.status}`);
  }

  const data = await response.json();
  const totalPages = Number(response.headers.get("X-WP-TotalPages") || 1);

  return {
    data: Array.isArray(data) ? data : [],
    totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1,
  };
};

const buildPageItems = (currentPage, totalPages) => {
  if (totalPages <= 1) {
    return [1];
  }

  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pageItems = [1];
  const leftSibling = Math.max(2, currentPage - 1);
  const rightSibling = Math.min(totalPages - 1, currentPage + 1);

  if (leftSibling > 2) {
    pageItems.push("…");
  }

  for (let page = leftSibling; page <= rightSibling; page += 1) {
    pageItems.push(page);
  }

  if (rightSibling < totalPages - 1) {
    pageItems.push("…");
  }

  pageItems.push(totalPages);

  return pageItems;
};

const ChevronLeft = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 sm:h-8 sm:w-8">
    <path d="M14.5 6.5 9 12l5.5 5.5" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.4" />
  </svg>
);

const ChevronRight = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 sm:h-8 sm:w-8">
    <path d="m9.5 6.5 5.5 5.5-5.5 5.5" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.4" />
  </svg>
);

const Blogs = () => {
  const [categories, setCategories] = useState([{ id: "all", label: "Semua" }]);
  const [activeCategoryId, setActiveCategoryId] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [postsPerPage, setPostsPerPage] = useState(() =>
    typeof window === "undefined" ? POSTS_PER_PAGE : getResponsivePostsPerPage(window.innerWidth)
  );

  useEffect(() => {
    const updatePostsPerPage = () => {
      setPostsPerPage(getResponsivePostsPerPage(window.innerWidth));
    };

    updatePostsPerPage();
    window.addEventListener("resize", updatePostsPerPage);

    return () => window.removeEventListener("resize", updatePostsPerPage);
  }, []);

  useEffect(() => {
    const debounceTimer = window.setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(debounceTimer);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategoryId, searchTerm, postsPerPage]);

  useEffect(() => {
    const wordpressBaseUrl = normalizeWordPressBaseUrl(process.env.REACT_APP_WORDPRESS_API_URL);

    if (!wordpressBaseUrl) {
      setPosts([]);
      setIsLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    const cachedCategories = readJsonCache(CATEGORY_CACHE_STORAGE_KEY, []);

    if (Array.isArray(cachedCategories) && cachedCategories.length > 0) {
      setCategories([{ id: "all", label: "Semua" }, ...cachedCategories]);
    }

    const loadCategories = async () => {
      try {
        const data = await fetchWordPressCategories(wordpressBaseUrl, controller.signal);

        if (data.length > 0) {
          setCategories([{ id: "all", label: "Semua" }, ...data]);
          writeJsonCache(CATEGORY_CACHE_STORAGE_KEY, data);
        }
      } catch (error) {
        if (error.name !== "AbortError" && cachedCategories.length === 0) {
          setCategories([{ id: "all", label: "Semua" }]);
        }
      }
    };

    loadCategories();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const wordpressBaseUrl = normalizeWordPressBaseUrl(process.env.REACT_APP_WORDPRESS_API_URL);

    if (!wordpressBaseUrl) {
      setPosts([]);
      setIsLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    const loadPosts = async () => {
      try {
        const selectedCategoryId = activeCategoryId === "all" ? null : activeCategoryId;
        const normalizedSearchTerm = normalizeSearchTerm(searchTerm);
        const cachedEntry = readPageCacheEntry(selectedCategoryId, normalizedSearchTerm, currentPage, postsPerPage);

        if (cachedEntry) {
          setPosts(cachedEntry.posts || []);
          setTotalPages(Number(cachedEntry.totalPages) || 1);
          setIsLoading(false);
        } else {
          setIsLoading(true);
        }

        const data = await fetchWordPressPostsPage(
          wordpressBaseUrl,
          currentPage,
          selectedCategoryId,
          normalizedSearchTerm,
          postsPerPage,
          controller.signal
        );
        const publishedPosts = data.data.filter(isPublishedPost);
        const mappedPosts = publishedPosts.map(mapWordPressPost);

        writePageCacheEntry({
          key: getPageCacheKey(selectedCategoryId, normalizedSearchTerm, currentPage, postsPerPage),
          categoryId: selectedCategoryId,
          searchTerm: normalizedSearchTerm,
          page: currentPage,
          postsPerPage,
          totalPages: data.totalPages,
          posts: mappedPosts,
          updatedAt: Date.now(),
        });

        setPosts(mappedPosts);
        setTotalPages(data.totalPages);
      } catch (error) {
        if (error.name !== "AbortError") {
          const cachedEntry = readPageCacheEntry(
            activeCategoryId === "all" ? null : activeCategoryId,
            normalizeSearchTerm(searchTerm),
            currentPage,
            postsPerPage
          );

          if (cachedEntry) {
            setPosts(cachedEntry.posts || []);
            setTotalPages(Number(cachedEntry.totalPages) || 1);
            return;
          }

          setPosts([]);
          setTotalPages(1);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadPosts();

    return () => controller.abort();
  }, [activeCategoryId, currentPage, postsPerPage, searchTerm]);

  const visiblePosts = useMemo(() => posts, [posts]);

  const paginationItems = useMemo(() => buildPageItems(currentPage, totalPages), [currentPage, totalPages]);

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === currentPage) {
      return;
    }

    setCurrentPage(nextPage);
  };

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  return (
    <section id="blog" className="relative overflow-hidden bg-[var(--st-color-cream)] px-4 py-12 sm:px-6 sm:py-14 lg:px-8 lg:py-16">
      <div className="pointer-events-none absolute left-[-8rem] top-[6.5rem] hidden w-[18rem] md:block lg:left-[-9rem] lg:top-[5rem] lg:w-[28rem]">
        <img src={FourPointStar} alt="" aria-hidden="true" className="w-full select-none" />
      </div>
      <div className="pointer-events-none absolute bottom-[-2rem] right-[-5rem] rotate-[20deg] hidden w-[18rem] md:block lg:bottom-[-0.25rem] lg:right-[-4rem] lg:w-[28rem]">
        <img src={CrookedStar} alt="" aria-hidden="true" className="w-full select-none" />
      </div>

      <div className="relative mx-auto w-full max-w-7xl">
        <div className="mx-auto max-w-[1360px]">
          <div className="mx-auto flex w-full max-w-[1440px] items-center rounded-[999px] border border-[#d7dee8] bg-white px-5 py-4 shadow-[0_10px_24px_rgba(52,58,78,0.12)] sm:px-6 sm:py-5 lg:px-7 lg:py-6">
            <svg viewBox="0 0 24 24" aria-hidden="true" className="h-6 w-6 shrink-0 text-[var(--st-color-red)] sm:h-7 sm:w-7">
              <path d="M10.5 3.5a7 7 0 1 0 4.47 12.36l4.33 4.33 1.41-1.41-4.33-4.33A7 7 0 0 0 10.5 3.5Zm0 2a5 5 0 1 1 0 10 5 5 0 0 1 0-10Z" fill="currentColor" />
            </svg>
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Cari artikel..."
              aria-label="Cari artikel"
              className="ml-4 min-w-0 flex-1 border-0 bg-transparent text-[18px] text-[#22242a] placeholder:text-[#98a2b3] outline-none sm:text-[20px] lg:text-[22px]"
            />
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 sm:mt-10 lg:mt-12 lg:gap-4">
            {categories.map((category) => {
              const isActive = category.id === activeCategoryId;

              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => {
                    setCurrentPage(1);
                    setActiveCategoryId(category.id);
                  }}
                  className={`rounded-full border px-6 py-3 text-[15px] font-medium transition-colors duration-200 sm:px-7 sm:py-3.5 sm:text-[16px] ${
                    isActive
                      ? "border-[var(--st-color-red)] bg-[var(--st-color-red)] text-white"
                      : "border-[var(--st-color-red)] bg-transparent text-[var(--st-color-red)] hover:bg-[rgba(233,60,36,0.08)]"
                  }`}
                >
                  {category.label}
                </button>
              );
            })}
          </div>

          {isLoading ? (
            <div className="mt-10 grid gap-6 sm:mt-12 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={`blog-skeleton-${index}`} className="overflow-hidden rounded-[22px] border border-[#d9dfe9] bg-white shadow-[0_10px_22px_rgba(36,41,56,0.06)]">
                  <div className="h-[180px] animate-pulse bg-[#eef2f7] sm:h-[200px] lg:h-[180px] xl:h-[200px]" />
                  <div className="space-y-4 px-5 pb-5 pt-4 sm:px-6 sm:pb-6 sm:pt-5">
                    <div className="h-6 w-24 animate-pulse rounded-full bg-[#eef2f7]" />
                    <div className="h-7 w-full animate-pulse rounded-full bg-[#eef2f7]" />
                    <div className="h-7 w-5/6 animate-pulse rounded-full bg-[#eef2f7]" />
                    <div className="h-16 w-full animate-pulse rounded-[18px] bg-[#eef2f7]" />
                  </div>
                </div>
              ))}
            </div>
          ) : visiblePosts.length > 0 ? (
            <div className="mt-10 grid gap-6 sm:mt-12 sm:grid-cols-2 lg:grid-cols-3 lg:gap-7">
              {visiblePosts.map((post) => (
                <BlogCard key={`${post.title}-${post.date}`} post={post} />
              ))}
            </div>
          ) : (
            <div className="mt-10 rounded-[22px] border border-[#d9dfe9] bg-white px-6 py-10 text-center text-[#6e7687] shadow-[0_10px_22px_rgba(36,41,56,0.06)] sm:mt-12">
              {searchTerm ? "Tidak ada artikel yang cocok." : "Artikel belum tersedia."}
            </div>
          )}

          <div className="mt-10 flex items-center justify-center gap-2 sm:mt-12 lg:mt-14">
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex h-11 min-w-11 items-center justify-center rounded-[10px] border font-medium transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50 sm:h-12 sm:min-w-12 border-[#cfd6e3] bg-white text-[#6f7687] transition-colors duration-200 hover:border-[var(--st-color-red)] hover:text-[var(--st-color-red)]"
            >
              <ChevronLeft />
            </button>

            {paginationItems.map((item, index) => {
              const isNumber = /^\d+$/.test(String(item));
              const isActive = Number(item) === currentPage;

              return (
                <button
                  key={`page-${item}-${index}`}
                  type="button"
                  onClick={() => {
                    if (isNumber) {
                      handlePageChange(Number(item));
                    }
                  }}
                  disabled={!isNumber}
                  className={`flex h-11 min-w-11 items-center justify-center rounded-[10px] border font-medium transition-colors duration-200 sm:h-12 sm:min-w-12 ${
                    isActive
                      ? "border-[var(--st-color-red)] bg-[var(--st-color-red)] text-white"
                      : isNumber
                        ? "border-[#cfd6e3] bg-white text-[15px] text-[#6f7687] hover:border-[var(--st-color-red)] hover:text-[var(--st-color-red)] sm:text-[16px]"
                        : "border-transparent bg-transparent text-[15px] text-[#6f7687] sm:text-[16px]"
                  }`}
                >
                  {item}
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex h-11 min-w-11 items-center justify-center rounded-[10px] border font-medium transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50 sm:h-12 sm:min-w-12 border-[#cfd6e3] bg-white text-[#6f7687] transition-colors duration-200 hover:border-[var(--st-color-red)] hover:text-[var(--st-color-red)]"
            >
              <ChevronRight />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Blogs;